<script module lang="ts">
  import type { AllowedUserShow, TableBreakpoint, UserBadgeData, UserTooltipField } from './values.types';

  export interface Props {
    user: UserBadgeData | null | undefined;
    /** Defaults to `username`. */
    show?: AllowedUserShow;
    /** Hides the name below the given breakpoint (defaults to `nav` if true), only the acronym stays. */
    compact?: boolean | TableBreakpoint;
    /** Whether to show a tooltip with user details (defaults to true when show is 'acronym', or list of fields to show). */
    tooltip?: boolean | UserTooltipField[];
    /** Explicit list of fields to show in the tooltip (e.g. ['username', 'id']). */
    tooltipFields?: UserTooltipField[];
  }
</script>

<script lang="ts">
  import { CommonTexts } from '../../services/CommonTexts';
  import { allowedUserShows } from './values.types';
  import { UserService } from '../../services/UserService.svelte';
  import Tooltip from '../tooltips/Tooltip.svelte';
  import Color from './Color.svelte';

  const { user, show, compact = false, tooltip, tooltipFields }: Props = $props();

  const DefaultShow = allowedUserShows[0];
  const UseShow = $derived(show && allowedUserShows.includes(show) ? show : DefaultShow);
  const Name = $derived(user ? (UseShow === 'name' ? UserService.getDisplayName(user) : user.username) : '');

  const EffectiveTooltipFields = $derived.by((): UserTooltipField[] => {
    if (tooltipFields !== undefined) return tooltipFields;
    if (Array.isArray(tooltip)) return tooltip;
    if (tooltip === true) return ['username', 'person', 'id'];
    if (tooltip === false) return [];
    return UseShow === 'acronym' || Boolean(compact) ? ['username', 'person', 'id'] : [];
  });

  const ShowTooltip = $derived(Boolean(user && EffectiveTooltipFields.length > 0));

  const ShowUsernameInTooltip = $derived(EffectiveTooltipFields.includes('username'));
  const PersonName = $derived([user?.titles, user?.given_name, user?.family_name].filter(Boolean).join(' '));
  const ShowPersonInTooltip = $derived(EffectiveTooltipFields.includes('person') && PersonName !== '');
  const ShowIdInTooltip = $derived(
    Boolean(
      (EffectiveTooltipFields.includes('id') || EffectiveTooltipFields.includes('user_id')) && user && user.user_id !== undefined && user.user_id !== null && user.user_id !== ''
    )
  );
  const ShowNameInTooltip = $derived(EffectiveTooltipFields.includes('name'));
  const ShowAcronymInTooltip = $derived(Boolean(EffectiveTooltipFields.includes('acronym') && user?.acronym));
  const ShowColorInTooltip = $derived(Boolean(EffectiveTooltipFields.includes('color') && user?.color));
</script>

{#if user}
  {#if ShowTooltip}
    <Tooltip placement="top" toggleOnTouch={true} touchTimeout={30}>
      {#snippet content()}
        {#if ShowUsernameInTooltip}
          <div class="tooltipRow">
            <span class="tooltipLabel">{CommonTexts.User}:</span>
            <span class="tooltipValue">{user.username}</span>
          </div>
        {/if}
        {#if ShowPersonInTooltip}
          <div class="tooltipRow">
            <span class="tooltipLabel">{CommonTexts.Person}:</span>
            <span class="tooltipValue">{PersonName}</span>
          </div>
        {/if}
        {#if ShowNameInTooltip}
          <div class="tooltipRow">
            <span class="tooltipLabel">{CommonTexts.Name}:</span>
            <span class="tooltipValue">{UserService.getDisplayName(user)}</span>
          </div>
        {/if}
        {#if ShowAcronymInTooltip}
          <div class="tooltipRow">
            <span class="tooltipLabel">{CommonTexts.Acronym}:</span>
            <span class="tooltipValue mono">{user.acronym}</span>
          </div>
        {/if}
        {#if ShowColorInTooltip}
          <div class="tooltipRow">
            <span class="tooltipLabel">{CommonTexts.Color}:</span>
            <Color value={user.color} />
          </div>
        {/if}
        {#if ShowIdInTooltip}
          <div class="tooltipRow">
            <span class="tooltipLabel">{CommonTexts.UserId}:</span>
            <span class="tooltipValue mono">{user.user_id}</span>
          </div>
        {/if}
      {/snippet}
      <span
        class="userBadge"
        class:compact={compact === true || compact === 'nav'}
        class:compactMini={compact === 'mini'}
        class:compactSmall={compact === 'small'}
        class:compactMedium={compact === 'medium'}
        class:compactLarge={compact === 'large'}
        class:compactXlarge={compact === 'xlarge'}>
        <span class="acronym" style:background-color={user.color ?? undefined}><span>{user.acronym ?? user.username.slice(0, 3)}</span></span>
        {#if UseShow !== 'acronym'}
          <span class="name">{Name}</span>
        {/if}
      </span>
    </Tooltip>
  {:else}
    <span
      class="userBadge"
      class:compact={compact === true || compact === 'nav'}
      class:compactMini={compact === 'mini'}
      class:compactSmall={compact === 'small'}
      class:compactMedium={compact === 'medium'}
      class:compactLarge={compact === 'large'}
      class:compactXlarge={compact === 'xlarge'}>
      <span class="acronym" style:background-color={user.color ?? undefined}><span>{user.acronym ?? user.username.slice(0, 3)}</span></span>
      {#if UseShow !== 'acronym'}
        <span class="name">{Name}</span>
      {/if}
    </span>
  {/if}
{:else}
  <span>{CommonTexts.Empty}</span>
{/if}

<style lang="scss">
  @use '../../../styles/mixins';

  .userBadge {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.35em;
    white-space: nowrap;
    font-size: var(--font-size);
    line-height: 1;
    vertical-align: middle;
    cursor: default;

    @include mixins.greaterMedium {
      gap: 0.5em;
    }

    .acronym {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      background: #1a251e;
      border-radius: 50%;
      width: 1.5em;
      height: 1.5em;
      line-height: 1;
      vertical-align: middle;
      cursor: default;
      user-select: none;
      box-sizing: border-box;

      span {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        font-size: 0.55em;
        line-height: 1;
        font-weight: bold;
        color: #fff;
        cursor: default;
        text-align: center;
        text-transform: uppercase;
      }
    }

    .name {
      display: inline-flex;
      align-items: center;
      line-height: 1.2;
      vertical-align: middle;
    }

    &.compact .name {
      display: none;

      @include mixins.greaterNav() {
        display: inline-flex;
      }
    }

    &.compactMini .name {
      display: none;

      @include mixins.greaterMini() {
        display: inline-flex;
      }
    }

    &.compactSmall .name {
      display: none;

      @include mixins.greaterSmall() {
        display: inline-flex;
      }
    }

    &.compactMedium .name {
      display: none;

      @include mixins.greaterMedium() {
        display: inline-flex;
      }
    }

    &.compactLarge .name {
      display: none;

      @include mixins.greaterLarge() {
        display: inline-flex;
      }
    }

    &.compactXlarge .name {
      display: none;

      @include mixins.greaterXlarge() {
        display: inline-flex;
      }
    }
  }
</style>
